import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { Combobox, Dialog, Listbox, Transition } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import React, { Fragment, useMemo, useState, memo } from "react";

import { setLoading } from "@/app_redux/reducers/loading_reducer";

import { useNavigate } from "react-router-dom";

import { useSearchCustomerAPI } from "@/app/hooks/api_hooks/customer_profile/useSearchCustomerAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useMappedConstants } from "@/app/hooks/useMappedConstants";
import { useRouter } from "@/app/hooks/useRouter";
import useScreenSize from "@/app/hooks/useScreenSize";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useUtils } from "@/app/hooks/useUtils";
import { VMS_CLICK_EVENTS, VMS_SCREEN_IDS, useVMSClickLog } from "@/app/hooks/useVMSClickLog";
import {
  setCustomerAddresses,
  setCustomerDetails,
  setCustomerMembershipDetails,
  setCustomerMembershipStatus,
  setCustomerSearchQuery,
  setCustomerSearchQueryToggle,
  setPetDetails,
} from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { setShowCommandPallete } from "@/app_redux/reducers/slices/ui_states_slice";
import { BiLoaderAlt } from "react-icons/bi";
import { FcAssistant } from "react-icons/fc";
import { Tooltip } from "react-tooltip";
import { ICommandPalleteProps } from "../interface";
import { ICustomerAddresses, ICustomerDetailsCustomerProfile } from "@/api_framework/api_modals/Users";

const CommandPalleteComponent: React.FC<ICommandPalleteProps> = (_props) => {
  const { setVisible: setSpeechRecognitionVisible } = useModalActions("SPEECH_RECOGNITION_MODAL");
  const { classNames } = useUtils();
  const { isMobile } = useScreenSize();
  const isSearchInProgress = useLoader("isCustomerSearchInProgress");
  const { getRouteKey } = useRouter();
  const { vmsPlaceholders } = useMappedConstants();
  const { customerToggleCode } = useStringConstants();
  const { searchCustomerToggleCommandPallete } = useArrayConstants();
  const [selected, setSelected] = useState(searchCustomerToggleCommandPallete[0]);

  const [searchQuery, setSearchQuery] = useState<string>("");

  const commandPalleteShow = useAppSelector((state) => state.root.UIStates.commandPallete?.show ?? false);
  const { execute: customerSearchAPI } = useSearchCustomerAPI();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { logVMSClick } = useVMSClickLog(VMS_SCREEN_IDS.HEADER);

  const handleSetQuery = (queryString: string) => {
    setSearchQuery(queryString);
    dispatch(setCustomerSearchQuery(queryString));
  };

  // Any because it's coming from constants.ts
  const handleSearchToggle = (toggleItem: any) => {
    logVMSClick(VMS_CLICK_EVENTS.HEADER_SEARCH_TYPE_CHANGE, toggleItem.title);
    dispatch(setCustomerSearchQueryToggle(toggleItem.toggleCode));
    setSelected(toggleItem);
  };

  const setCustomerDetailsData = (
    customerDetails: ICustomerDetailsCustomerProfile,
    addresses: ICustomerAddresses,
    membershipStatus: boolean,
    membershipDetail: string,
  ) => {
    dispatch(
      setCustomerMembershipStatus({
        status: membershipStatus,
      }),
    );
    dispatch(
      setCustomerMembershipDetails({
        membershipDetails: membershipDetail,
      }),
    );
    dispatch(
      setCustomerDetails({
        customerDetails: customerDetails,
      }),
    );
    dispatch(
      setCustomerAddresses({
        addresses: addresses,
      }),
    );
  };
  const handleSearch = () => {
    setCustomerDetailsData(null, [], false, null);
    dispatch(
      setPetDetails({
        petDetails: null,
      }),
    );
    if (searchQuery !== "") {
      logVMSClick(VMS_CLICK_EVENTS.HEADER_SEARCH_SUBMIT, selected.title, {
        search_item: searchQuery,
        search_tag: selected.title,
      });
      dispatch(
        setLoading({
          key: "isCustomerSearchInProgress",
          status: true,
        }),
      );
      navigate(getRouteKey("SEARCH_CUSTOMER", "url"));
      customerSearchAPI(searchQuery, selected.toggleCode as "0" | "1").then(() => {
        dispatch(setShowCommandPallete(false));
        dispatch(setCustomerSearchQuery(searchQuery));
      });
    }
  };

  const handleSetShow = (showState: boolean) => {
    dispatch(setShowCommandPallete(showState));
  };

  const placeholder = useMemo(
    () =>
      selected.toggleCode === customerToggleCode
        ? vmsPlaceholders?.commandPalleteSearchByCustomerPlaceholder
        : vmsPlaceholders.commandPalleteSearchByPetPlaceholder,
    [
      selected.toggleCode,
      customerToggleCode,
      vmsPlaceholders?.commandPalleteSearchByCustomerPlaceholder,
      vmsPlaceholders.commandPalleteSearchByPetPlaceholder,
    ],
  );

  return (
    true && (
      <div className="hidden md:flex flex-1 min-w-[280px] max-w-2xl justify-start items-center">
        <div className="w-full ring-1 ring-gray-300 rounded-md flex justify-start items-center">
          {!isMobile && (
            <div className="">
              <Listbox value={selected} onChange={handleSearchToggle}>
                {({ open }) => (
                  <>
                    <Listbox.Label className="sr-only"></Listbox.Label>
                    <div className="relative">
                      <div className="inline-flex divide-x divide-gray-100 rounded-md shadow-sm">
                        <div className="inline-flex items-center gap-x-1.5 rounded-l-md bg-blue-50 px-3 py-2 text-gray-700 shadow-sm">
                          <p className=" font-normal text-xs">{selected.title}</p>
                        </div>
                        <Listbox.Button className="inline-flex items-center rounded-l-none  bg-blue-50 p-2  focus:outline-none   ">
                          <ChevronDownIcon className="h-3 w-3 text-gray-700" aria-hidden="true" />
                        </Listbox.Button>
                      </div>

                      <Transition
                        show={open}
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options className="absolute text-xs right-0 z-10 mt-2 w-32 origin-top-right divide-y divide-gray-200 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {searchCustomerToggleCommandPallete.map((option) => (
                            <Listbox.Option
                              key={option.title}
                              className={({ active }) =>
                                classNames(
                                  "cursor-pointer",
                                  active ? "bg-gray-700 text-white" : "text-gray-900",
                                  "cursor-default select-none px-2 py-2 text-sm",
                                )
                              }
                              value={option}
                            >
                              {({ selected, active }) => (
                                <div className="flex flex-col">
                                  <div className="flex justify-between text-xs">
                                    <p className={selected ? "font-semibold" : "font-normal"}>{option.title}</p>
                                    {selected ? (
                                      <span className={active ? "text-white" : "text-indigo-600"}>
                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              )}
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </Transition>
                    </div>
                  </>
                )}
              </Listbox>
            </div>
          )}
          <div className="relative w-full flex items-center">
            <input
              type="text"
              name="search"
              placeholder={placeholder}
              value={searchQuery}
              id="search"
              onKeyDown={(e) => {
                if (e.code === "Enter") {
                  handleSearch();
                }
              }}
              onChange={(event) => {
                handleSetQuery(event.target.value);
              }}
              className="block cursor-pointer w-full rounded-l-none rounded-r-md border-0 py-1.5 pr-14 text-gray-900 shadow-sm  outline-none border-none  placeholder:text-gray-400  text-xs 2xl:text-sm sm:leading-6"
            />
            <div className="absolute inset-y-0 right-0 flex py-1.5 pr-1.5">
              <button
                onClick={() => {
                  logVMSClick(VMS_CLICK_EVENTS.HEADER_VETIC_ASSISTANT_CLICK, "Vetic Assistant");
                  setSpeechRecognitionVisible();
                }}
                data-tooltip-id="assistant"
                data-tooltip-content="Vetic Assistant"
                className="cursor-pointer px-2 bg-white"
              >
                <FcAssistant className="w-4 h-4  md:w-4 md:h-4 lg:w-4 lg:h-4 xl:w-5 xl:h-5 2xl:h-6 2xl:w-6" />
              </button>
              <Tooltip style={{ fontSize: "10px" }} id="assistant" />
              {isSearchInProgress ? (
                <span className="animate-spin text-xl p-2 flex justify-center items-center flex-col">
                  <span className="text-xl text-blue-500">
                    <BiLoaderAlt />
                  </span>
                </span>
              ) : (
                <kbd className="hidden 2xl:inline-flex items-center rounded border border-gray-200 px-1 font-sans text-xs text-gray-400 ">
                  ⌘K
                </kbd>
              )}
            </div>
          </div>
        </div>
        <Transition.Root show={commandPalleteShow} as={Fragment} afterLeave={() => handleSetQuery("")} appear>
          <Dialog as="div" className="relative z-50" onClose={handleSetShow}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 backdrop-blur-sm bg-opacity-25 transition-opacity" />
            </Transition.Child>
            <div className="fixed inset-0 z-50 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="mx-auto max-w-xl transform overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
                  <Combobox onChange={(_item) => { }}>
                    <div className="relative">
                      <MagnifyingGlassIcon
                        className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                      <Combobox.Input
                        className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                        placeholder={placeholder}
                        value={searchQuery}
                        onKeyDown={(e) => {
                          if (e.code === "Enter") {
                            handleSearch();
                          }
                        }}
                        onChange={(event) => {
                          handleSetQuery(event.target.value);
                        }}
                      />
                    </div>
                  </Combobox>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>
      </div>
    )
  );
};

export default memo(CommandPalleteComponent);
